import { Currency, CurrencyAmount, JSBI, Pair, Percent, TokenAmount } from 'vvs-sdk'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import { usePair } from 'hooks/usePairs'
import useTotalSupply from 'hooks/useTotalSupply'

import { useTranslation } from 'contexts/Localization'
import useRefresh from 'hooks/useRefresh'
import { DeserializedRemovalOrder, DeserializedRemovalOrderUserData, SerializedRemovalOrder } from 'state/types'
import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'utils/bigNumber'
import { useWeb3React } from '@web3-react/core'
import { farmsConfig } from 'config/constants'
import { getAddress } from 'utils/addressHelpers'

import { TransactionType } from 'state/info/types'
import { mapMints, mapBurns } from './queries/helpers'
import { AppDispatch, AppState, useAppDispatch } from '../index'
import { tryParseAmount } from '../swap/hooks'
import { useTokenBalances } from '../wallet/hooks'
import {
  EditingField,
  fetchRemovalOrderUserDataAsync,
  Field,
  ClpField,
  RemovalField,
  RemovalMode,
  snapshotRemovalOrder,
  typeInput,
  typeSmartRemovalInput,
} from './actions'
import { fetchMintsBurns } from './queries/initialDeposit'
import { fetchPairRedemptions, RedemptionResults } from './queries/pairRedemption'

const lpAddresses = farmsConfig.map((farmToFetch) => getAddress(farmToFetch.lpAddresses))

export const usePollRemovalOrdersWithUserData = () => {
  const dispatch = useAppDispatch()
  const { slowRefresh } = useRefresh()
  const { account } = useWeb3React()

  useEffect(() => {
    if (account) {
      dispatch(fetchRemovalOrderUserDataAsync({ account, lpAddresses }))
    }
  }, [dispatch, slowRefresh, account])
}

export function useBurnState(): AppState['burn'] {
  return useSelector<AppState, AppState['burn']>((state) => state.burn)
}

export function useDerivedBurnInfo(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  removalMode: RemovalMode,
): {
  pair?: Pair | null
  parsedAmounts: {
    [Field.LIQUIDITY_PERCENT]: Percent
    [Field.LIQUIDITY]?: TokenAmount
    [Field.CURRENCY_A]?: CurrencyAmount
    [Field.CURRENCY_B]?: CurrencyAmount
  }
  error?: string
  removalInfo: {
    [RemovalField.TARGET_PRICE_TOKEN_A]: string
    [RemovalField.TARGET_PRICE_TOKEN_B]: string
    [RemovalField.DELTA_TOKEN_A]: number
    [RemovalField.DELTA_TOKEN_B]: number
  }
  inputTokenA: string
  inputTokenB: string
  editing: EditingField
} {
  const { account, chainId } = useActiveWeb3React()

  const { independentField, typedValue, inputTokenA, inputTokenB, editing } = useBurnState()

  const { t } = useTranslation()

  // pair + totalsupply
  const [, pair] = usePair(currencyA, currencyB)

  // removal orders
  const orders = useRemovalOrders()

  // balances
  const relevantTokenBalances = useTokenBalances(account ?? undefined, [pair?.liquidityToken])
  const userLiquidity: undefined | TokenAmount =
    removalMode === RemovalMode.RESUBMIT
      ? new TokenAmount(
          pair.liquidityToken,
          orders
            .find((r) => r.lpAddress.toLowerCase() === pair.liquidityToken.address.toLowerCase())
            .userData.stakedBalance.toString(),
        )
      : relevantTokenBalances?.[pair?.liquidityToken?.address ?? '']

  const [tokenA, tokenB] = [wrappedCurrency(currencyA, chainId), wrappedCurrency(currencyB, chainId)]
  const tokens = {
    [Field.CURRENCY_A]: tokenA,
    [Field.CURRENCY_B]: tokenB,
    [Field.LIQUIDITY]: pair?.liquidityToken,
  }

  // liquidity values
  const totalSupply = useTotalSupply(pair?.liquidityToken)
  const liquidityValueA =
    pair &&
    totalSupply &&
    userLiquidity &&
    tokenA &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalSupply.raw, userLiquidity.raw)
      ? new TokenAmount(tokenA, pair.getLiquidityValue(tokenA, totalSupply, userLiquidity, false).raw)
      : undefined
  const liquidityValueB =
    pair &&
    totalSupply &&
    userLiquidity &&
    tokenB &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalSupply.raw, userLiquidity.raw)
      ? new TokenAmount(tokenB, pair.getLiquidityValue(tokenB, totalSupply, userLiquidity, false).raw)
      : undefined
  const liquidityValues: { [Field.CURRENCY_A]?: TokenAmount; [Field.CURRENCY_B]?: TokenAmount } = {
    [Field.CURRENCY_A]: liquidityValueA,
    [Field.CURRENCY_B]: liquidityValueB,
  }

  let percentToRemove: Percent = new Percent('0', '100')
  // user specified a %
  if (independentField === Field.LIQUIDITY_PERCENT) {
    percentToRemove = new Percent(typedValue, '100')
  }
  // user specified a specific amount of liquidity tokens
  else if (independentField === Field.LIQUIDITY) {
    if (pair?.liquidityToken) {
      const independentAmount = tryParseAmount(typedValue, pair.liquidityToken)
      if (
        independentAmount &&
        userLiquidity &&
        (removalMode === RemovalMode.RESUBMIT || !independentAmount.greaterThan(userLiquidity))
      ) {
        percentToRemove = new Percent(independentAmount.raw, userLiquidity.raw)
      }
    }
  }
  // user specified a specific amount of token a or b
  else if (tokens[independentField]) {
    const independentAmount = tryParseAmount(typedValue, tokens[independentField])
    const liquidityValue = liquidityValues[independentField]
    if (independentAmount && liquidityValue && !independentAmount.greaterThan(liquidityValue)) {
      percentToRemove = new Percent(independentAmount.raw, liquidityValue.raw)
    }
  }

  const parsedAmounts: {
    [Field.LIQUIDITY_PERCENT]: Percent
    [Field.LIQUIDITY]?: TokenAmount
    [Field.CURRENCY_A]?: TokenAmount
    [Field.CURRENCY_B]?: TokenAmount
  } = {
    [Field.LIQUIDITY_PERCENT]: percentToRemove,
    [Field.LIQUIDITY]:
      userLiquidity && percentToRemove && percentToRemove.greaterThan('0')
        ? new TokenAmount(userLiquidity.token, percentToRemove.multiply(userLiquidity.raw).quotient)
        : undefined,
    [Field.CURRENCY_A]:
      tokenA && percentToRemove && percentToRemove.greaterThan('0') && liquidityValueA
        ? new TokenAmount(tokenA, percentToRemove.multiply(liquidityValueA.raw).quotient)
        : undefined,
    [Field.CURRENCY_B]:
      tokenB && percentToRemove && percentToRemove.greaterThan('0') && liquidityValueB
        ? new TokenAmount(tokenB, percentToRemove.multiply(liquidityValueB.raw).quotient)
        : undefined,
  }

  const removalInfo: {
    [RemovalField.TARGET_PRICE_TOKEN_A]: string
    [RemovalField.TARGET_PRICE_TOKEN_B]: string
    [RemovalField.DELTA_TOKEN_A]: number
    [RemovalField.DELTA_TOKEN_B]: number
  } = {
    [RemovalField.TARGET_PRICE_TOKEN_A]: tryParseAmount(inputTokenB, currencyB)
      ?.divide(tryParseAmount(inputTokenA, currencyA) ?? '1')
      .toSignificant(6),
    [RemovalField.TARGET_PRICE_TOKEN_B]: tryParseAmount(inputTokenA, currencyA)
      ?.divide(tryParseAmount(inputTokenB, currencyB) ?? '1')
      .toSignificant(6),
    [RemovalField.DELTA_TOKEN_A]:
      inputTokenA && parsedAmounts[Field.CURRENCY_A]
        ? ((parseFloat(inputTokenA) - parseFloat(parsedAmounts[Field.CURRENCY_A].toSignificant(6))) /
            parseFloat(parsedAmounts[Field.CURRENCY_A].toSignificant(6))) *
          100
        : 0,
    [RemovalField.DELTA_TOKEN_B]:
      inputTokenB && parsedAmounts[Field.CURRENCY_B]
        ? ((parseFloat(inputTokenB) - parseFloat(parsedAmounts[Field.CURRENCY_B].toSignificant(6))) /
            parseFloat(parsedAmounts[Field.CURRENCY_B].toSignificant(6))) *
          100
        : 0,
  }

  let error: string | undefined
  if (!account) {
    error = t('Connect Wallet')
  }

  if (removalMode === RemovalMode.SMART) {
    if (!parsedAmounts[Field.LIQUIDITY]) {
      error = error ?? t('Enter LP token amount')
    } else if (!inputTokenA || !inputTokenB) {
      error = error ?? t('Enter target token amount')
    }
  } else if (removalMode === RemovalMode.RESUBMIT && (!inputTokenA || !inputTokenB)) {
    error = error ?? t('Enter target token amount')
  } else if (!parsedAmounts[Field.LIQUIDITY]) {
    error = error ?? t('Enter an amount')
  }

  if (removalMode !== RemovalMode.INSTANT) {
    // Validate smart removal input
    const parsedAmountA = tryParseAmount(inputTokenA, currencyA ?? undefined)
    const parsedAmountB = tryParseAmount(inputTokenB, currencyB ?? undefined)
    if (!parsedAmountA || !parsedAmountB) {
      error = error ?? t('Token decimals overflow')
    }
  }

  return { pair, parsedAmounts, error, removalInfo, inputTokenA, inputTokenB, editing }
}

export function useBurnActionHandlers(): {
  onUserInput: (field: Field, typedValue: string) => void
} {
  const dispatch = useDispatch<AppDispatch>()

  const onUserInput = useCallback(
    (field: Field, typedValue: string) => {
      dispatch(typeInput({ field, typedValue }))
    },
    [dispatch],
  )

  return {
    onUserInput,
  }
}

export function useSmartRemovalInputActionHandlers(): {
  onUserInput: (field: ClpField, typedValue: string) => void
} {
  const dispatch = useDispatch<AppDispatch>()

  const onUserInput = useCallback(
    (field: ClpField, typedValue: string) => {
      dispatch(typeSmartRemovalInput({ field, typedValue }))
    },
    [dispatch],
  )

  return {
    onUserInput,
  }
}

export const useRemovalOrders = (): DeserializedRemovalOrder[] => {
  const burns = useBurnState()
  return burns.removalOrders.map(deserializeRemovalOrder)
}

export const useRemovalOrderFromLpAddress = (lpAddress: string): DeserializedRemovalOrder => {
  const burns = useBurnState()
  return deserializeRemovalOrder(
    burns.removalOrders.find((f) => f.lpAddress === lpAddress) ?? burns.removalOrders.find((f) => f.lpAddress === ''),
  )
}

export const useRemovalOrderUser = (lpAddress): DeserializedRemovalOrderUserData => {
  const { userData } = useRemovalOrderFromLpAddress(lpAddress)
  const { allowance, stakedBalance, hasRuleEnabled, token0Amt, token1Amt, pendingVvs } = userData
  return {
    allowance,
    stakedBalance,
    hasRuleEnabled,
    token0Amt,
    token1Amt,
    pendingVvs,
  }
}

const deserializeRemovalOrderUserData = (order: SerializedRemovalOrder): DeserializedRemovalOrderUserData => {
  return {
    allowance: order.userData ? new BigNumber(order.userData.allowance) : BIG_ZERO,
    stakedBalance: order.userData ? new BigNumber(order.userData.stakedBalance) : BIG_ZERO,
    hasRuleEnabled: order.userData?.hasRuleEnabled,
    token0Amt: order.userData ? new BigNumber(order.userData.token0Amt) : BIG_ZERO,
    token1Amt: order.userData ? new BigNumber(order.userData.token1Amt) : BIG_ZERO,
    pendingVvs: order.userData ? new BigNumber(order.userData.pendingVvs) : BIG_ZERO,
  }
}

const deserializeRemovalOrder = (order: SerializedRemovalOrder): DeserializedRemovalOrder => {
  const { lpAddress } = order
  return {
    lpAddress,
    userData: deserializeRemovalOrderUserData(order),
  }
}

export const useSnapshot = (key: string) => {
  const { snapshot } = useBurnState()
  if (!snapshot) {
    return null
  }
  return snapshot[key]
}

export const useSnapshotActionHandler = () => {
  const dispatch = useDispatch<AppDispatch>()

  const handleSnapshot = (
    key: string,
    info: {
      inputTokenA: string
      inputTokenB: string
      TARGET_PRICE_TOKEN_A: string
      TARGET_PRICE_TOKEN_B: string
      DELTA_TOKEN_A: number
      DELTA_TOKEN_B: number
    },
  ) => {
    dispatch(snapshotRemovalOrder({ key, info }))
  }

  return { onSnapshot: handleSnapshot }
}

export const useFetchedInitialDeposit = () => {
  const fetch = async (
    acc,
    lpAddrs,
  ): Promise<
    [
      initialDeposits: {
        type: TransactionType
        amountToken0: BigNumber
        amountToken1: BigNumber
        liquidity: BigNumber
      },
      error: boolean,
    ]
  > => {
    const { data: mintsBurnsData, error } = await fetchMintsBurns(acc, lpAddrs.toLowerCase())
    const mints = mintsBurnsData.mints.map(mapMints)
    const burns = mintsBurnsData.burns.map(mapBurns)
    const initialDeposits = mints
      .concat(burns)
      .map((x) => ({
        type: x.type,
        amountToken0: x.amountToken0,
        amountToken1: x.amountToken1,
        liquidity: x.liquidity,
      }))
      .reduce(
        (prev, curr) => {
          const isBurn = curr.type === TransactionType.BURN
          return {
            ...prev,
            amountToken0: prev.amountToken0.plus(isBurn ? curr.amountToken0.negated() : curr.amountToken0),
            amountToken1: prev.amountToken1.plus(isBurn ? curr.amountToken1.negated() : curr.amountToken1),
            liquidity: prev.liquidity.plus(isBurn ? curr.liquidity.negated() : curr.liquidity),
          }
        },
        {
          type: TransactionType.MINT,
          amountToken0: new BigNumber('0'),
          amountToken1: new BigNumber('0'),
          liquidity: new BigNumber('0'),
        },
      )

    return [initialDeposits, error]
  }

  return { onFetch: fetch }
}

export const useFetchedPairRedemptions = (
  account: string,
): [data: RedemptionResults, error: boolean, loading: boolean] => {
  const [data, setData] = useState<RedemptionResults>()
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  async function fetch(acc) {
    setLoading(true)
    const { data: redemptionsData, error: fetchError } = await fetchPairRedemptions(acc)
    setData(redemptionsData)
    setLoading(false)

    if (fetchError) {
      setError(true)
    }
  }

  useEffect(() => {
    if (account) {
      fetch(account)
    }
  }, [account])

  return [data, error, loading]
}
